import type { BabbelTheme } from '@lessonnine/design-tokens.lib/dist/json/theme';
import type { Property } from 'csstype';
import type { ColorCSSVar, ObjectOrAnySubPath, ObjectWithNewLeafType } from './types';

// Utility function to transform a token path to a suitable CSS variable name
const tokenPathToCSSVariableName = (tokenPath: string[]) => tokenPath.join('-');

const isColorProperty = (tokens: ObjectOrAnySubPath<BabbelTheme>): tokens is Property.Color =>
  typeof tokens === 'string';

/*
  Function to create an array of CSS variables out of the given tokens.
  Will be called recursively internally.
  Example:
  constructCSSVariablesArray({
    interactive: {
      primary: '#fff',
      disabled: '#ccc',
    }
  })
  will output [{ name: 'interactive.primary', value: '#fff' }, { name: 'interactive.disabled', value: '#ccc' }]
*/
const constructCSSVariablesArray = (
  tokens: ObjectOrAnySubPath<BabbelTheme>,
  currentPath: string[] = []
): ColorCSSVar[] => {
  if (isColorProperty(tokens)) {
    /*
      If we reach the color property, we exhausted this path and we can create our structure for our CSS variable
      containing its name and its value
    */
    return [{ name: tokenPathToCSSVariableName(currentPath), value: tokens }];
  }
  // Otherwise we go through all entries recursively and create a flattened array out of it
  return Object.entries(tokens).reduce(
    (colorVarsArray, [pathSegment, value]) => [
      ...colorVarsArray,
      ...constructCSSVariablesArray(value, [...currentPath, pathSegment]),
    ],
    [] as ColorCSSVar[]
  );
};

/*
  Given a Babbel theme, will generate CSS variables definition for each token of the theme
*/
export const createCSSVariablesDefinition = (tokens: BabbelTheme) =>
  constructCSSVariablesArray(tokens)
    .map(({ name, value }) => `--${name}: ${value};`)
    .join('\n');

/*
  Given a Babbel theme, will map every token to its CSS Variable usage.
  Example:
  mapToCSSVariableUsage({
    interactive: {
      primary: '#fff',
      disabled: '#ccc',
    }
  })
  will return 
  {
    interactive: {
      primary: 'var(--interactive-primary)',
      disabled: 'var(--interactive-disabled)',
    }
  }
*/
export const mapToCSSVariableUsage = <
  T extends Exclude<ObjectOrAnySubPath<BabbelTheme>, Property.Color>
>(
  tokens: T,
  currentPath: string[] = []
): ObjectWithNewLeafType<T, string> =>
  // Loop recursively through all entries
  Object.entries(tokens).reduce(
    (tokensAsCSSVars, [keySegment, value]) => ({
      ...tokensAsCSSVars,
      ...{
        [keySegment]:
          // For each value, if it is a leaf value replace it with the CSS variable usage,
          // Otherwise, call the function recursively
          isColorProperty(value)
            ? `var(--${tokenPathToCSSVariableName([...currentPath, keySegment])})`
            : mapToCSSVariableUsage(value, [...currentPath, keySegment]),
      },
    }),
    {} as ObjectWithNewLeafType<T, string>
  );
